import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RoutesRecognized } from '@angular/router';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, filter, pairwise } from 'rxjs/operators';
import * as _ from 'underscore';
import { AuthService } from '../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../@core/utils/error.service';
import { Brewery } from '../../../../customer-integrations/hollingbery/modules/brewery-configuration/models/brewery';
import { BreweryService } from '../../../../customer-integrations/hollingbery/modules/brewery-configuration/service/brewery.service';
import { Cart } from '../../../../customer-integrations/hollingbery/modules/financial/orders/models/cart';
import { CartApiService } from '../../../../customer-integrations/hollingbery/modules/financial/orders/services/cart.service';
import { Customer } from '../../../modules/contracts/models/customer';
import { CustomerService } from '../../../modules/contracts/services/customer.service';
import { estimateTypeDTO } from '../../../modules/estimates/models/estimate-type-dto';
import { EstimatesService } from '../../../modules/estimates/services/estimates.service';
import { FarmFeature, FarmFeatureType } from '../../../modules/farm-features/models/farm-feature-dto';
import { FarmFeatureEventType } from '../../../modules/farm-features/models/farm-feature-event';
import { FarmFeatureService } from '../../../modules/farm-features/services/farm-feature.service';
import { FieldEventType } from '../../../modules/field-events/models/field-event-type';
import { FieldEventTypesService } from '../../../modules/field-events/services/field-event-types.service';
import { InventoryLotDTO } from '../../../modules/inventory/models/inventory-lot';
import { InventoryItemDTO } from '../../../modules/inventory/models/item';
import { Carrier } from '../../../modules/pick-plan/models/carrier';
import { PoolTypeDTO } from '../../../modules/pick-plan/models/pool-type-dto';
import { StorageTypeDTO } from '../../../modules/pick-plan/models/storage-types-dto';
import { TruckType } from '../../../modules/pick-plan/models/truck-type';
import { carrierService } from '../../../modules/pick-plan/services/carrier.service';
import { InventoryService } from '../../../modules/pick-plan/services/inventory.service';
import { truckTypeService } from '../../../modules/pick-plan/services/truck-type.service';
import { QcArea } from '../../../modules/quality-control/models/qc-master';
import { QualityControlStructureService } from '../../../modules/quality-control/services/quality-control-structure.service';
import { WorkOrderType } from '../../../modules/work-orders/models/work-order';
import { WorkOrderTypeService } from '../../../modules/work-orders/services/work-order-type.service';
import { BlockCustomFieldValueDTO, BlockDTO } from '../../models/block';
import { Crop } from '../../models/crop';
import { CropVariety } from '../../models/crop-variety';
import { CropYear } from '../../models/cropYear';
import { FarmDTO } from '../../models/farm';
import { Room } from '../../models/storage/room';
import { RoomLocation } from '../../models/storage/room-location';
import { Warehouse } from '../../models/storage/warehouse';
import { TenantWordsPipe } from '../../pipes/tenant-words.pipe';
import { BlockService } from '../../services/block.service';
import { CropYearService } from '../../services/crop-year.service';
import { CropService } from '../../services/crop.service';
import { PlatformConfigService } from '../../services/platform-config.service';
import { PoolService } from '../../services/pool.service';
import { StorageService } from '../../services/storage.service';
import { RoomService } from '../../services/storage/room-service';
import { WarehouseService } from '../../services/storage/warehouse-service';
import { VarietyService } from '../../services/variety.service';
import { FilterResults } from './models/filter-results';
import { FilterItem } from './models/kw-filter-item';

export interface FilterRetainerRoute {
  sourceUrl: string;
  destinationUrl: string;
  bothDirections: boolean;
}

export interface BlockCustomFieldDropdownValue {
  name: string;
  id: number;
  data: FilterItem[];
}

// Dropdowns whose cache can be kept in sync with upsertCached()/removeCached()
export type FilterCacheType =
  'crop' | 'estimateType' | 'warehouse' | 'room' | 'roomLocation' | 'inventoryItem' | 'lot' |
  'carrier' | 'truckType' | 'poolType' | 'storageType' | 'workOrderType' | 'fieldEventType' |
  'customer' | 'brewery' | 'featureType' | 'feature' | 'featureEventType';

interface FilterCacheConfig {
  all: string;           // cached records (all*)
  idKey: string;         // id property on a cached record
  list: string;          // dropdown items (listOf*)
  selected: string;      // selected items (selected*), or null when handled by rebuild
  rebuild: () => void;
  prepare?: (item: any) => any;
}

@Injectable({
  providedIn: 'root'
})
export class KwFilterDropdownService {
  private destroyRef = inject(DestroyRef);
  public useVarietyShortName: boolean = false;

  // When the host page hides the crop selector, do not silently scope the variety
  // dropdown to the default crop — varieties from other crops would appear on the
  // map/table but be missing from the filter. Component sets this from
  // `filterSettings.cropSettings?.display`.
  public cropFilterVisible: boolean = true;

  constructor(
    private blocksService: BlockService,
    private poolService: PoolService,
    private cropYearService: CropYearService,
    private pipe: TenantWordsPipe,
    private estimateService: EstimatesService,
    private CropService: CropService,
    private errorService: ErrorService,
    private QcStructureService: QualityControlStructureService,
    private warehouseService: WarehouseService,
    private inventoryService: InventoryService,
    private carrierService: carrierService,
    private truckTypeService: truckTypeService,
    private roomService: RoomService,
    private cartService: CartApiService,
    private breweryService: BreweryService,
    private router: Router,
    private authService: AuthService,
    private farmFeatureService: FarmFeatureService,
    private varietyService: VarietyService,
    private platformConfig: PlatformConfigService,
    private storageService: StorageService,
    private workOrderTypeService: WorkOrderTypeService,
    private fieldEventTypeService: FieldEventTypesService,
    private customerService: CustomerService,
    private platformConfigService: PlatformConfigService
  ) {
    // Load retain checkbox state and saved filters from localStorage
    this.bypassFilterReset = this.loadRetainCheckboxState();
    if (this.bypassFilterReset) {
      this.loadFiltersFromStorage();
    }

    this.platformConfigService.getAllConfigValues().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(platformConfig => {
      const config = platformConfig.find(x => x.name === 'useVarietyShortName');
      this.useVarietyShortName = config ? config.value === 'true' : false;
    });

    let routerSubscription = this.router.events
      .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise(), takeUntilDestroyed(this.destroyRef))
      .subscribe((events: RoutesRecognized[]) => {

        //--------- Do not believe this is needed anymore, but leaving it in for now ------------

        // let sourceUrl = events[0].urlAfterRedirects;
        // let destinationUrl = events[1].urlAfterRedirects;
        // let bypassFilterReset = true;

        // // check to see if it's a bypass route
        // for (let bypass of this.filterRetainerRoutes) {

        //   if (sourceUrl.includes(bypass.sourceUrl) && destinationUrl.includes(bypass.destinationUrl)) {
        //     bypassFilterReset = true;
        //   }

        //   // if it is flagged as both directions, check the inverse
        //   if (bypass.bothDirections) {
        //     if (destinationUrl.includes(bypass.sourceUrl) && sourceUrl.includes(bypass.destinationUrl)) {
        //       bypassFilterReset = true;
        //     }
        //   }

        // }

        // add if statement to retain filters permanently HE-492

        
        if (!this.bypassFilterReset) {
          this.resetDropdowns();
          this.selectedDates = [null, null];
        }

      });

  }

  onCheckboxChange(checked: boolean): void {
    this.bypassFilterReset = checked;
    this.saveRetainCheckboxState(checked);
    if (!checked) {
      this.clearSavedFilters();
    }
  }
  getRetainFilters(): boolean {
    return this.bypassFilterReset;
  }

  // localStorage persistence methods
  private readonly STORAGE_KEY_FILTERS = 'kw_filter_state';
  private readonly STORAGE_KEY_RETAIN_CHECKBOX = 'kw_retain_filters_checked';

  private saveRetainCheckboxState(checked: boolean): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_RETAIN_CHECKBOX, JSON.stringify(checked));
    } catch (e) {
      console.warn('Failed to save retain checkbox state to localStorage:', e);
    }
  }

  private loadRetainCheckboxState(): boolean {
    try {
      var saved = localStorage.getItem(this.STORAGE_KEY_RETAIN_CHECKBOX);
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load retain checkbox state from localStorage:', e);
    }
    return true; // default to true
  }

  private saveFiltersToStorage(): void {
    if (!this.bypassFilterReset) {
      return;
    }
    try {
      var state = {
        selectedFarms: this.selectedFarms,
        selectedBlocks: this.selectedBlocks,
        selectedVarieties: this.selectedVarieties,
        selectedFieldmen: this.selectedFieldmen,
        selectedCropYear: this.selectedCropYear,
        selectedCrop: this.selectedCrop,
        selectedWarehouses: this.selectedWarehouses,
        selectedRooms: this.selectedRooms,
        selectedInventoryItem: this.selectedInventoryItem,
        selectedLocation: this.selectedLocation,
        selectedLotNumber: this.selectedLotNumber,
        selectedEstimateTypes: this.selectedEstimateTypes,
        selectedQcAreas: this.selectedQcAreas,
        selectedCarrier: this.selectedCarrier,
        selectedTruckType: this.selectedTruckType,
        selectedOrganicConventional: this.selectedOrganicConventional,
        selectedPoolTypes: this.selectedPoolTypes,
        selectedStorageTypes: this.selectedStorageTypes,
        selectedWorkOrderTypes: this.selectedWorkOrderTypes,
        selectedGrouping: this.selectedGrouping,
        selectedFeatures: this.selectedFeatures,
        selectedFeatureTypes: this.selectedFeatureTypes,
        selectedFeatureEventType: this.selectedFeatureEventType,
        selectedCustomer: this.selectedCustomer,
        selectedFieldEventType: this.selectedFieldEventType,
        barcode: this.barcode,
        selectedDates: this.selectedDates
      };
      localStorage.setItem(this.STORAGE_KEY_FILTERS, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save filters to localStorage:', e);
    }
  }

  private loadFiltersFromStorage(): boolean {
    try {
      var saved = localStorage.getItem(this.STORAGE_KEY_FILTERS);
      if (saved) {
        var state = JSON.parse(saved);
        // Helper to filter out placeholder items with id: 0 or 'No data found' text
        var filterPlaceholders = function(arr: any[]) {
          if (!arr || !Array.isArray(arr)) return [];
          return arr.filter(function(item) {
            return item && item.id !== 0 && item.itemName !== 'No data found';
          });
        };
        this.selectedFarms = filterPlaceholders(state.selectedFarms);
        this.selectedBlocks = filterPlaceholders(state.selectedBlocks);
        this.selectedVarieties = filterPlaceholders(state.selectedVarieties);
        this.selectedFieldmen = state.selectedFieldmen || [];
        this.selectedCropYear = state.selectedCropYear || [];
        this.selectedCrop = state.selectedCrop || [{ itemName: 'Select', id: 0 }];
        this.selectedWarehouses = filterPlaceholders(state.selectedWarehouses);
        this.selectedRooms = filterPlaceholders(state.selectedRooms);
        this.selectedInventoryItem = filterPlaceholders(state.selectedInventoryItem);
        this.selectedLocation = filterPlaceholders(state.selectedLocation);
        this.selectedLotNumber = filterPlaceholders(state.selectedLotNumber);
        this.selectedEstimateTypes = state.selectedEstimateTypes || [{ itemName: 'Final', id: 0 }];
        this.selectedQcAreas = state.selectedQcAreas || [];
        this.selectedCarrier = state.selectedCarrier || [];
        this.selectedTruckType = state.selectedTruckType || [];
        this.selectedOrganicConventional = state.selectedOrganicConventional || [];
        this.selectedPoolTypes = state.selectedPoolTypes || [];
        this.selectedStorageTypes = state.selectedStorageTypes || [];
        this.selectedWorkOrderTypes = state.selectedWorkOrderTypes || [];
        this.selectedGrouping = state.selectedGrouping || [];
        this.selectedFeatures = state.selectedFeatures || [];
        this.selectedFeatureTypes = state.selectedFeatureTypes || [];
        this.selectedFeatureEventType = state.selectedFeatureEventType || [];
        this.selectedCustomer = state.selectedCustomer || [];
        this.selectedFieldEventType = state.selectedFieldEventType || [];
        this.barcode = state.barcode || '';
        // Restore dates, converting string back to Date objects
        if (state.selectedDates && state.selectedDates.length === 2) {
          this.selectedDates = [
            state.selectedDates[0] ? new Date(state.selectedDates[0]) : null,
            state.selectedDates[1] ? new Date(state.selectedDates[1]) : null
          ];
        }
        return true;
      }
    } catch (e) {
      console.warn('Failed to load filters from localStorage:', e);
    }
    return false;
  }

  private clearSavedFilters(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_FILTERS);
    } catch (e) {
      console.warn('Failed to clear saved filters from localStorage:', e);
    }
  }

  getFilterState(): FilterResults {
    this.filterResults.selectedDateRange = this.selectedDates;
    return this.filterResults;
  }

  //Only reload varieties (For when a new variety is created)
  reloadVarieties() {
    this.isLoading = true;
    this.varietyService.getVarieties(true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.allVarieties = data;
      this.allVarieties = _.sortBy(this.allVarieties, 'name');
      var varietyMap = [...this.allVarieties].map(x => {
        if (this.useVarietyShortName && x.varietyShortName) {
          return {
            id: x.id,
            itemName: x.varietyShortName
          };
        }
        else {
          return {
            id: x.id,
            itemName: x.name
          };
        }
      });
      var uniqVarieties = _.uniq(varietyMap, x => x.id);
      this.listOfVarieties = _.sortBy(uniqVarieties, x => x.itemName);
      this.isLoading = false;
    })
  }

  // Insert/replace one variety in the cache and rebuild the dropdown (no API call).
  // Called by the Varieties page as soon as a save is submitted so filter pages never show a stale value.
  public upsertVariety(variety: CropVariety) {
    if (!variety || !variety.id) return;
    this.setVarieties([...this.allVarieties.filter(x => x.id !== variety.id), variety]);
  }

  public removeVariety(id: number) {
    this.setVarieties(this.allVarieties.filter(x => x.id !== id));
  }

  public setVarieties(varieties: CropVariety[]) {
    this.allVarieties = _.sortBy(varieties || [], 'name');
    this.buildVarietyList();
    this.syncBlockVarieties();
  }

  // Blocks carry a copy of their variety's name/crop (shown as "Block (Variety)" in the Block dropdown);
  // keep it in sync when varieties change, otherwise block labels stay stale.
  private syncBlockVarieties() {
    let changed = false;
    const blocks = this.allBlocks.map(b => {
      const v = this.allVarieties.find(x => x.id === b.varietyId);
      if (v && (v.name !== b.varietyName || v.cropId !== b.cropId)) {
        changed = true;
        return { ...b, varietyName: v.name, cropId: v.cropId };
      }
      return b;
    });
    if (changed) this.setBlocks(blocks);
  }

  // Insert/replace one block in the cache and rebuild the block-derived dropdowns (no API call).
  // Inactive blocks are removed, since the filter only loads active blocks.
  public upsertBlock(block: BlockDTO) {
    if (!block || !block.id) return;
    // The dialog/POST echo don't carry cropId; the server derives it from the variety (Variety.CropId),
    // so mirror that here or filterBlocks() drops the block under the selected crop.
    const variety = this.allVarieties.find(v => v.id === block.varietyId);
    const cached = { ...block, cropId: variety ? variety.cropId : block.cropId };
    const others = this.allBlocks.filter(x => x.id !== block.id);
    this.setBlocks(cached.isActive === false ? others : [...others, cached]);
  }

  public removeBlock(id: number) {
    this.setBlocks(this.allBlocks.filter(x => x.id !== id));
  }

  private setBlocks(blocks: BlockDTO[]) {
    this.allBlocks = blocks;
    this.buildFarmList();
    this.filterBlocks();
    this.filterFieldman();
    // Keep already-selected blocks labelled with their current name/variety
    this.selectedBlocks = this.selectedBlocks.map(s => this.listOfBlocks.find(b => b.id === s.id) || s);
    // The crop year list is scoped by the blocks' crops, so a block/variety crop change can change it
    this.syncCropYears();
  }

  // Re-fetch crop years only (for when the server may have created new ones, e.g. after a variety's
  // crop changes FixCropYears() can create CropYears for the new crop that the cache doesn't know about).
  public refreshCropYears() {
    // Not loaded yet: loadAllData() will fetch current crop years anyway
    if (!this.hasLoaded) return;
    this.cropYearService.getCropYears().pipe(
      catchError((err) => {
        this.errorService.handleError(err);
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(years => {
      if (!years) return;
      this.allYears = years;
      this.syncCropYears();
    });
  }

  // Rebuild the crop year dropdown from the cache and make sure the selection is still valid (no API call).
  public syncCropYears() {
    if (!this.hasLoaded) return;
    this.buildCropYearList();
    this.reconcileSelectedCropYear();
  }

  private buildCropYearList() {
    let resultBlocks = this.allBlocks.filter(x => this.selectedBlocks.filter(y => y.id != 0).map(y => y.id).includes(x.id));

    let sortedYears = _.sortBy(this.allYears, x => x.year).reverse();

    const selectedCropId = this.selectedCrop.length > 0 && this.selectedCrop[0] ? this.selectedCrop[0].id : null;
    sortedYears = sortedYears.filter(x => x.cropId === selectedCropId);

    if (resultBlocks.length > 0) {
      this.listOfYears = [...sortedYears.filter(x => resultBlocks.map(y => y.cropId).includes(x.cropId))].map(x => <FilterItem>{
        id: x.id,
        itemName: x.name,
        isActive: x.isCurrentYear,
      });
    } else if (this.allBlocks.length > 0) {
      this.listOfYears = [...sortedYears.filter(x => this.allBlocks.map(y => y.cropId).includes(x.cropId))].map(x => <FilterItem>{
        id: x.id,
        itemName: x.name,
        isActive: x.isCurrentYear,
      });
    } else {
      this.listOfYears = [...sortedYears].map(x => <FilterItem>{
        id: x.id,
        itemName: x.name,
        isActive: x.isCurrentYear,
      });
    }
  }

  // Keep the selected crop year pointing at an entry that exists in listOfYears, so consumers
  // (Estimates, Pick Plans, QC) never request a stale cropYearId. Falls back to the same year,
  // then the current year, then the latest year.
  private reconcileSelectedCropYear() {
    const previous = this.selectedCropYear.filter(x => x && x.id != 0);
    const previousIds = previous.map(x => x.id);

    let next = this.listOfYears.filter(y => previousIds.includes(y.id));
    if (next.length === 0) {
      const previousNames = previous.map(x => x.itemName);
      const fallback = this.listOfYears.find(y => previousNames.includes(y.itemName))
        || this.listOfYears.find(y => y.isActive == true)
        || this.listOfYears[0];
      next = fallback ? [fallback] : [];
    }

    const changed = next.map(x => x.id).join() !== previousIds.join();
    this.selectedCropYear = next;

    if (changed) {
      // Pushes the new selection to filterResults, notifies subscribed pages and persists it
      this.change();
    } else {
      this.filterResults.selectedCropYears = this.selectedCropYear.filter(x => x.id != 0);
    }
  }

  // Insert/replace one cached record of the given dropdown type and rebuild that dropdown (no API call).
  // Called by admin pages after a successful save so filter pages never show a stale value.
  public upsertCached(type: FilterCacheType, item: any) {
    const config = this.cacheConfig(type);
    // Some endpoints return a single-item list for a single record - unwrap defensively
    const value = Array.isArray(item) ? item[0] : item;
    // Not loaded yet: loadAllData() will fetch current data anyway
    if (!this.hasLoaded || !value || value[config.idKey] == null) return;

    const all: any[] = (this as any)[config.all] || [];
    const existing = all.find(x => x[config.idKey] === value[config.idKey]);
    // Merge over the cached copy so fields the save response doesn't echo back are kept
    let cached = existing ? { ...existing, ...value } : value;
    if (config.prepare) cached = config.prepare(cached);

    // Replace in place to keep the server's ordering for unsorted dropdowns
    (this as any)[config.all] = existing
      ? all.map(x => x[config.idKey] === cached[config.idKey] ? cached : x)
      : [...all, cached];
    this.applyCacheChange(config);
  }

  // Remove one cached record of the given dropdown type (e.g. after a delete) and rebuild that dropdown.
  public removeCached(type: FilterCacheType, id: any) {
    const config = this.cacheConfig(type);
    if (!this.hasLoaded || id == null) return;
    const all: any[] = (this as any)[config.all] || [];
    (this as any)[config.all] = all.filter(x => x[config.idKey] !== id);
    this.applyCacheChange(config);
  }

  // Farms have no cache of their own - the Farm dropdown is built from the blocks - so a farm rename
  // is applied to the blocks' copy of the farm name (same approach as syncBlockVarieties).
  public syncFarm(farm: FarmDTO) {
    if (!this.hasLoaded || !farm || !farm.id) return;
    if (!this.allBlocks.some(b => b.farmId === farm.id && b.farmName !== farm.name)) return;
    this.setBlocks(this.allBlocks.map(b => b.farmId === farm.id ? { ...b, farmName: farm.name } : b));
    // Keep already-selected farms labelled with their current name
    this.selectedFarms = this.selectedFarms.map(s => this.listOfFarms.find(f => f.id === s.id) || s);
  }

  private applyCacheChange(config: FilterCacheConfig) {
    config.rebuild();
    if (config.selected) {
      this.reconcileSelection(config);
    }
  }

  // Re-label selected items from the rebuilt dropdown and drop ones that no longer exist, so pages
  // never filter on a deleted record. Placeholders (id 0, e.g. "Select" / "Final") are kept.
  private reconcileSelection(config: FilterCacheConfig) {
    const selected: FilterItem[] = (this as any)[config.selected] || [];
    const list: FilterItem[] = (this as any)[config.list] || [];
    const all: any[] = (this as any)[config.all] || [];

    const next = selected
      .filter(s => s && (s.id === 0 || all.some(x => x[config.idKey] === s.id)))
      .map(s => list.find(x => x.id === s.id && s.id !== 0) || s);
    (this as any)[config.selected] = next;

    if (next.length !== selected.length) {
      // Pushes the new selection to filterResults, notifies subscribed pages and persists it
      this.change();
    }
  }

  private cacheConfig(type: FilterCacheType): FilterCacheConfig {
    switch (type) {
      case 'crop':
        return { all: 'allCrops', idKey: 'id', list: 'listOfCrops', selected: null, rebuild: () => this.syncCrops() };
      case 'estimateType':
        return { all: 'allEstimateTypes', idKey: 'id', list: 'listOfEstimateTypes', selected: 'selectedEstimateTypes', rebuild: () => this.filterEstimateTypes() };
      case 'warehouse':
        return { all: 'allWarehouses', idKey: 'id', list: 'listOfWarehouses', selected: 'selectedWarehouses', rebuild: () => this.syncWarehouses() };
      case 'room':
        return {
          all: 'allRooms', idKey: 'id', list: 'listOfRooms', selected: 'selectedRooms', rebuild: () => this.syncRooms(),
          // Room dropdown is grouped by warehouse name; the save response may not include the warehouse
          prepare: (room: Room) => room.warehouse ? room : { ...room, warehouse: this.allWarehouses.find(w => w.id === room.warehouseId) }
        };
      case 'roomLocation':
        return {
          all: 'allLocations', idKey: 'id', list: 'listOfLocations', selected: 'selectedLocation', rebuild: () => this.filterRoomLocations(),
          // Location dropdown is grouped by room name; the save response may not include the room
          prepare: (location: RoomLocation) => location.room ? location : { ...location, room: this.allRooms.find(r => r.id === location.roomId) || <Room>{ name: '' } }
        };
      case 'inventoryItem':
        return { all: 'allInventoryItems', idKey: 'id', list: 'listOfInventoryItems', selected: 'selectedInventoryItem', rebuild: () => this.buildInventoryItemList() };
      case 'lot':
        return { all: 'allLots', idKey: 'id', list: 'listOfLotNumbers', selected: 'selectedLotNumber', rebuild: () => this.buildLotList() };
      case 'carrier':
        return { all: 'allCarriers', idKey: 'id', list: 'listOfCarriers', selected: 'selectedCarrier', rebuild: () => this.buildCarrierList() };
      case 'truckType':
        return { all: 'allTruckTypes', idKey: 'id', list: 'listOfTruckTypes', selected: 'selectedTruckType', rebuild: () => this.buildTruckTypeList() };
      case 'poolType':
        return { all: 'allPoolTypes', idKey: 'id', list: 'listOfPoolTypes', selected: 'selectedPoolTypes', rebuild: () => this.buildPoolTypeList() };
      case 'storageType':
        return { all: 'allStorageTypes', idKey: 'id', list: 'listOfStorageTypes', selected: 'selectedStorageTypes', rebuild: () => this.buildStorageTypeList() };
      case 'workOrderType':
        return { all: 'allWorkOrderTypes', idKey: 'id', list: 'listOfWorkOrderTypes', selected: 'selectedWorkOrderTypes', rebuild: () => this.buildWorkOrderTypeList() };
      case 'fieldEventType':
        return { all: 'allFieldEventTypes', idKey: 'id', list: 'listOfFieldEventTypes', selected: 'selectedFieldEventType', rebuild: () => this.buildFieldEventTypeList() };
      case 'customer':
        return { all: 'allCustomers', idKey: 'id', list: 'listOfCustomers', selected: 'selectedCustomer', rebuild: () => this.buildCustomerList() };
      case 'brewery':
        return { all: 'allBreweries', idKey: 'recordId', list: 'listOfBreweries', selected: 'selectedBrewery', rebuild: () => this.buildBreweryList() };
      case 'featureType':
        return { all: 'allFeatureTypes', idKey: 'farmFeatureTypeId', list: 'listOfFeatureTypes', selected: 'selectedFeatureTypes', rebuild: () => this.buildFeatureTypeList() };
      case 'feature':
        return { all: 'allFeatures', idKey: 'farmFeatureId', list: 'listOfFeatures', selected: 'selectedFeatures', rebuild: () => this.buildFeatureList() };
      case 'featureEventType':
        return { all: 'allFeatureEventTypes', idKey: 'id', list: 'listOfFeatureEventTypes', selected: 'selectedFeatureEventType', rebuild: () => this.buildFeatureEventTypeList() };
    }
  }

  private syncCrops() {
    this.buildCropList();
    const selectedCropId = this.selectedCrop.length > 0 && this.selectedCrop[0] ? this.selectedCrop[0].id : null;
    const selected = this.listOfCrops.find(x => x.id === selectedCropId);
    if (selected) {
      // Keep the selected crop labelled with its current name
      this.selectedCrop = [selected];
      return;
    }
    // Selected crop was deleted: fall back to the default crop and its crop years, as a reload would
    const defaultCrop = this.allCrops.find(x => x.isDefault === true) || this.allCrops[0];
    if (defaultCrop && selectedCropId !== 0) {
      this.selectedCrop = [{ id: defaultCrop.id, itemName: defaultCrop.name }];
      this.filterEstimateTypes();
      this.filterCropYearByCropID();
    }
  }

  // Rooms carry a copy of their warehouse (the Room dropdown is grouped by warehouse name)
  private syncWarehouses() {
    this.buildWarehouseList();
    this.allRooms = this.allRooms.map(r => {
      const warehouseId = r.warehouse ? r.warehouse.id : r.warehouseId;
      const w = this.allWarehouses.find(x => x.id === warehouseId);
      return w && r.warehouse && r.warehouse.name !== w.name ? { ...r, warehouse: { ...r.warehouse, name: w.name } } : r;
    });
    this.filterRooms();
    this.filterRoomLocations();
  }

  // Locations carry a copy of their room (the Location dropdown is grouped by room name)
  private syncRooms() {
    this.allLocations = this.allLocations.map(l => {
      const r = this.allRooms.find(x => x.id === l.roomId);
      return r && l.room && l.room.name !== r.name ? { ...l, room: { ...l.room, name: r.name } } : l;
    });
    this.filterRooms();
    this.filterRoomLocations();
  }

  private buildCropList() {
    var crop = [...this.allCrops].map(x => {
      return {
        id: x.id,
        itemName: x.name,
      };
    });
    var uniqCrops = _.uniq(crop, x => x.id);
    this.listOfCrops = _.sortBy(uniqCrops, x => x.itemName);
  }

  private buildCustomerList() {
    this.listOfCustomers = this.allCustomers.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
    });
  }

  private buildFieldEventTypeList() {
    var fieldEventTypes = [...this.allFieldEventTypes].map(x => {
      return {
        id: x.id,
        itemName: x.name,
      };
    });
    var uniqFieldEventTypes = _.uniq(fieldEventTypes, x => x.id);
    this.listOfFieldEventTypes = _.sortBy(uniqFieldEventTypes, x => x.itemName);
  }

  private buildStorageTypeList() {
    this.listOfStorageTypes = this.allStorageTypes.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
      group: ''
    });
  }

  private buildWorkOrderTypeList() {
    var workOrderTypeMap = [...this.allWorkOrderTypes].map(x => {
      return {
        id: x.id,
        itemName: x.name,
      };
    });
    var uniqWorkOrderTypes = _.uniq(workOrderTypeMap, x => x.id);
    this.listOfWorkOrderTypes = _.sortBy(uniqWorkOrderTypes, x => x.itemName);
  }

  private buildFeatureEventTypeList() {
    this.listOfFeatureEventTypes = this.allFeatureEventTypes.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
      group: x.farmFeatureTypeName
    });
  }

  private buildFeatureTypeList() {
    this.listOfFeatureTypes = this.allFeatureTypes.map(x => <FilterItem>{
      id: x.farmFeatureTypeId,
      itemName: x.name,
    });
  }

  private buildFeatureList() {
    this.listOfFeatures = this.allFeatures.map(x => <FilterItem>{
      id: x.farmFeatureId,
      itemName: x.name,
      group: x.farmName
    });
  }

  private buildPoolTypeList() {
    var poolMap = [...this.allPoolTypes].map(x => {
      return {
        id: x.id,
        itemName: x.name
      };
    });
    var uniqPools = _.uniq(poolMap, x => x.id);
    this.listOfPoolTypes = _.sortBy(uniqPools, x => x.itemName);
  }

  private buildCarrierList() {
    var carrierMap = [...this.allCarriers].map(x => {
      return {
        id: x.id,
        itemName: x.name
      };
    });
    var uniqCarriers = _.uniq(carrierMap, x => x.id);
    this.listOfCarriers = _.sortBy(uniqCarriers, x => x.itemName);
  }

  private buildTruckTypeList() {
    var truckTypeMap = [...this.allTruckTypes].map(x => {
      return {
        id: x.id,
        itemName: x.name
      };
    });
    var uniqTruckTypes = _.uniq(truckTypeMap, x => x.id);
    this.listOfTruckTypes = _.sortBy(uniqTruckTypes, x => x.itemName);
  }

  private buildWarehouseList() {
    this.listOfWarehouses = this.allWarehouses.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
    });
  }

  private buildInventoryItemList() {
    this.listOfInventoryItems = this.allInventoryItems.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
    });
  }

  private buildBreweryList() {
    this.listOfBreweries = _.sortBy(this.allBreweries.map(x => <FilterItem>{
      id: x.recordId,
      itemName: x.displayName,
    }), x => x.itemName);
  }

  private buildLotList() {
    this.listOfLotNumbers = this.allLots.map(x => <FilterItem>{
      id: x.id,
      itemName: x.lotNumber,
    });
  }

  private buildFarmList() {
    var farmMap = [...this.allBlocks].map(x => {
      return {
        id: x.farmId,
        itemName: x.farmName
      };
    });
    var uniqFarms = _.uniq(farmMap, x => x.id);
    this.listOfFarms = _.sortBy(uniqFarms, x => x.itemName);
  }

  private buildVarietyList() {
    // Show all varieties for the selected crop, not just those assigned to blocks
    const user = this.authService.getCurrentUser();
    const isStmlt5202Tenant = user && user.tenant && user.tenant.tenantShortCode === 'STMLT5202';

    if (isStmlt5202Tenant) {
      var varietyMap = [...this.allVarieties]
        .map(x => ({
          id: x.id,
          itemName: this.useVarietyShortName && x.varietyShortName ? x.varietyShortName : x.name
        }));
      var uniqVarieties = _.uniq(varietyMap, x => x.id);
      this.listOfVarieties = _.sortBy(uniqVarieties, x => x.itemName);
    } else {
      // Show all varieties filtered by selected crop only — but if the page hides
      // the crop selector, skip the scoping so varieties match what the page renders.
      var varietyMap = [...this.allVarieties]
        .filter(x => !this.cropFilterVisible || this.selectedCrop.length === 0 || this.selectedCrop[0].id === 0 || x.cropId === this.selectedCrop[0].id)
        .map(x => {
          return {
            id: x.id,
            itemName: this.useVarietyShortName && x.varietyShortName ? x.varietyShortName : x.name
          };
        });
      var uniqVarieties = _.uniq(varietyMap, x => x.id);
      this.listOfVarieties = _.sortBy(uniqVarieties, x => x.itemName);
    }

    // Keep already-selected varieties labelled with their current (possibly renamed) name
    this.selectedVarieties = this.selectedVarieties.map(s => this.listOfVarieties.find(v => v.id === s.id) || s);
  }

  loadAllData() {
    // Prevent duplicate API calls if already loading
    if (this.isLoadingData) {
      return;
    }
    this.isLoadingData = true;
    this.isLoading = true;

    this.platformConfig.getIsHops().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:(isHops) => {
      this.isHops = isHops;

      forkJoin( [
        // this.blocksService.apiBlocksGet(),

        this.blocksService.apiBlocksGetAll().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.cropYearService.getCropYears().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.CropService.getCrops().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.QcStructureService.getQcAreas().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.estimateService.getEstimateTypes().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.warehouseService.getWarehouses().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.truckTypeService.getTruckTypes().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.carrierService.getCarriers().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),

        this.inventoryService.getInventoryItems().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.inventoryService.getLotsForFilters().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.roomService.getRoomLocations().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        (this.authService.getCurrentUser().tenant.tenantId == '355809ec-3c76-4ef7-9341-b013755806f2' ? this.cartService.getCartReport() : of([])).pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        (this.authService.getCurrentUser().tenant.tenantId == '355809ec-3c76-4ef7-9341-b013755806f2' ? this.breweryService.getBreweryList() : of([])).pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        (this.authService.hasRole('farm-feature-events:read') ? this.farmFeatureService.getFarmFeatureEventTypes() : of([])).pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        (this.authService.hasRole('farm-features:read') ? this.farmFeatureService.getFarmFeatures() : of([])).pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        (this.authService.hasRole('farm-features:read') ? this.farmFeatureService.getFarmFeatureTypes() : of([])).pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.blocksService.getCustomFields().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.blocksService.getCustomFieldValues().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.poolService.getPoolTypes().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.varietyService.getVarieties().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.storageService.getStorageTypes().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.roomService.getRooms().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.workOrderTypeService.getWorkOrderTypes().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.fieldEventTypeService.apiFieldEventTypesGet().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        ),
        this.customerService.getCustomerList().pipe(
          catchError((err) => {
            this.errorService.handleError(err);
            return of([]);
          })
        )
        // this.farmFeatureService.getFarmFeatureEventTypes(),
      ]
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:(data: any) => {
        this.allBlocks = data[0];
        this.allYears = data[1];
        this.allCrops = data[2];
        this.allQcAreas = data[3];
        this.allEstimateTypes = data[4];

        this.allWarehouses = data[5];

        this.allTruckTypes = data[6];
        this.allCarriers = data[7];

        this.allInventoryItems = data[8];
        this.allLots = data[9];
        this.allLocations = data[10];

        this.allUserNames = data[11];
        this.allBreweries = data[12];

        this.allFeatureEventTypes = data[13];
        this.allFeatures = data[14];
        this.allFeatureTypes = data[15];
        this.allCustomers = data[24];
        this.allRooms = data[21];

        this.allCrops = _.sortBy(this.allCrops, 'name');
        this.allBlockCustomFieldValues = data[17];

        this.allPoolTypes = data[18];
        this.allPoolTypes = _.sortBy(this.allPoolTypes, 'name');

        this.allVarieties = data[19];
        this.allVarieties = _.sortBy(this.allVarieties, 'name');

        this.allStorageTypes = data[20];
        this.allStorageTypes = _.sortBy(this.allStorageTypes, 'name');

        this.allFieldEventTypes = data[23];
        this.allFieldEventTypes = _.sortBy(this.allFieldEventTypes, 'name');

        this.customBlockFields = data[16].map(x => {
          let values = _.uniq(
            this.allBlockCustomFieldValues
              .filter(field => field.blockCustomFieldId == x.id && field.value != null && field.value !== '')
              .map(x => x.value.trim())
          );


          let newValues = values.map(item => <FilterItem>{
            id: item,
            itemName: item
          });


          return {
            id: x.id,
            name: x.name,
            data: newValues
          };
        });

        this.allGroupings.push(
          {
            itemName: 'Block',
            id: 'blockName',
            group: ''
          },
          {
            itemName: 'Farm',
            id: 'farmName',
            group: ''
          },
          {
            itemName: 'Variety',
            id: 'varietyName',
            group: ''
          },
        )

        let customGroupings = data[16].map(x => {
          return {
            id: x.name,
            itemName: x.name,
            group: ''
          };
        });

        for (let x of customGroupings) {
          this.allGroupings.push(x);
        }

        this.allWorkOrderTypes = data[22];
        // for(let x of this.allWorkOrderTypes) {
        //   if(x.name == 'Greenhouse') {
        //     x.name = 'Plant-Out'
        //   }
        // }

        this.setDropdownLists();
      }, error:err => { 
        this.errorService.handleError(err); 
        this.isLoading = false; }});
    }, error:(err) => { 
      this.errorService.handleError(err); 
      this.isLoading = false; }});
  }

  public resetDropdowns(forceReset: boolean = false) {

    this.selectedGrouping = [];
    this.selectedFarms = [];
    this.selectedBlocks = this.defaultSelectedList;
    this.selectedBlockCustomFieldValue = [...this.customBlockFields.map(x => {
      return {
        id: x.id,
        name: x.name,
        data: []
      };
    })];

    this.selectedRooms = this.defaultSelectedList;
    this.selectedWarehouses = this.defaultSelectedList;
    this.selectedInventoryItem = this.defaultSelectedList;
    this.selectedLocation = this.defaultSelectedList;
    this.selectedLotNumber = [];
    this.selectedEstimateTypes = [{ itemName: 'Final', id: 0 }];
    this.selectedVarieties = [];
    this.selectedFieldEventType = [];
    this.selectedFieldmen = [];
    this.selectedUserName = [];
    this.selectedBrewery = [];
    this.selectedCarrier = [];
    this.selectedTruckType = [];
    this.selectedLoad = '';
    this.selectedCrop = this.defaultSelectedList;
    this.selectedTypes = [];
    this.selectedShipmentStatuses = this.defaultShipmentStatuses;
    this.selectedCropYear = [];
    this.selectedOrganicConventional = this.listOfOrganic.slice();
    this.selectedCompleted = [];
    this.selectedQcAreas = [];
    this.selectedFeatureTypes = [];
    this.selectedFeatures = [];
    this.selectedFeatureEventType = [];
    this.selectedDates =  [null, null];
    this.selectedCustomer = [];
    this.barcode = '';

    // Clear saved filters from localStorage when resetting
    this.clearSavedFilters();

    //this.loadAllData();
    this.setDropdownLists(forceReset);
  }

  public setDropdownLists(forceReset: boolean = false) {

    // if we flipped the bypass flag, reset it and skip over the resets

    if (this.bypassFilterReset && !this.initialLoad && !forceReset) {
      // this.bypassFilterReset = false;
      return;
    }

    // Check if we have saved filters to restore on initial load
    var hasSavedFilters = this.bypassFilterReset && this.initialLoad && localStorage.getItem(this.STORAGE_KEY_FILTERS);

    if (this.initialLoad) {
      this.initialLoad = false;
    }

    // Skip resetting selections if we have saved filters to restore
    if (!hasSavedFilters) {
      this.selectedCrop[0].id = this.allCrops.filter(x => x.isDefault === true)[0].id;
      this.selectedCrop[0].itemName = this.allCrops.filter(x => x.isDefault === true)[0].name;
      this.selectedBlocks = [];
      this.selectedBlockCustomFieldValue = [...this.customBlockFields.map(x => {
        return {
          id: x.id,
          name: x.name,
          data: []
        };
      })];
      this.selectedFeatureEventType = [];
      this.selectedWarehouses = [];
      this.selectedRooms = [];
      this.selectedInventoryItem = [];
      this.selectedLocation = [];
      this.selectedLotNumber = [];
      this.selectedFarms = [];
      this.selectedGrouping = [];
      this.selectedStorageTypes = [];
      this.selectedWorkOrderTypes = [];
    } else {
      // Validate saved crop still exists, otherwise use default
      var savedCropExists = this.selectedCrop && this.selectedCrop.length > 0 &&
        this.allCrops.some(x => x.id === this.selectedCrop[0].id);
      if (!savedCropExists) {
        this.selectedCrop[0].id = this.allCrops.filter(x => x.isDefault === true)[0].id;
        this.selectedCrop[0].itemName = this.allCrops.filter(x => x.isDefault === true)[0].name;
      }
      // Always initialize selectedBlockCustomFieldValue to match customBlockFields structure
      this.selectedBlockCustomFieldValue = [...this.customBlockFields.map(x => {
        return {
          id: x.id,
          name: x.name,
          data: []
        };
      })];
    }


    this.buildCropList();
    this.buildCustomerList();
    this.buildFieldEventTypeList();


    var blockMap = [...this.allBlocks].map(x => {
      return {
        id: x.id,
        itemName: x.name + " (" + x.varietyName + ")",
        group: x.farmName
      };
    });

    var uniqBlocks = _.uniq(blockMap, x => x.id);
    this.listOfBlocks = _.sortBy(uniqBlocks, x => x.group);

    /// set farms
    this.buildFarmList();

    this.buildStorageTypeList();
    this.buildWorkOrderTypeList();
    this.buildFeatureEventTypeList();
    this.buildFeatureTypeList();
    this.buildFeatureList();

    /// set varieties
    this.buildVarietyList();

    this.buildPoolTypeList();
    this.buildCarrierList();
    this.buildTruckTypeList();

    // set fieldmen
    var fieldmenMap = [...this.allBlocks].map(x => {
      return {
        id: x.fieldman || '',
        itemName: x.fieldman || 'None'
      };
    });
    var uniqFieldmen = _.uniq(fieldmenMap, x => x.id);
    this.listOfFieldmen = _.sortBy(uniqFieldmen, x => x.itemName);

    // set crop years
    this.buildCropYearList();

    // qc areas
    this.listOfQcAreas = _.sortBy(this.allQcAreas, x => x.name).map(x => <FilterItem>{
      id: x.id,
      itemName: x.name
    });

    this.buildWarehouseList();
    this.buildInventoryItemList();

    // usernames
    this.listOfUserNames = _.uniq(
      _.sortBy(
        this.allUserNames.map(x => <FilterItem>{
          id: x.id,
          itemName: x.userName,
        }),
        x => x.itemName
      ),
      x => x.itemName
    );

    this.buildBreweryList();

    // locations
    this.listOfLocations = this.allLocations.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
    });

    this.buildLotList();

    // room
    this.listOfRooms = this.allRooms.map(x => <FilterItem>{
      id: x.id,
      itemName: x.name,
      group: x && x.warehouse && x.warehouse.name
    });

    // Only set default crop year if we don't have saved filters or saved selection is invalid
    if (!hasSavedFilters || this.selectedCropYear.length === 0) {
      this.selectedCropYear = this.listOfYears.filter(x => x.isActive == true);
    } else {
      // Validate saved crop year still exists in the list
      var validCropYears = this.selectedCropYear.filter(saved =>
        this.listOfYears.some(y => y.id === saved.id)
      );
      if (validCropYears.length === 0) {
        this.selectedCropYear = this.listOfYears.filter(x => x.isActive == true);
      } else {
        this.selectedCropYear = validCropYears;
      }
    }

    // this.selectedCropYear = this.listOfYears.filter(x => x.itemName === new Date().getFullYear().toString());

    this.filterBlocks();
    this.filterRooms();
    this.filterRoomLocations();

    this.filterFieldman();
    this.filterEstimateTypes();

    // We do not need to emit the crop as it is being emitted via the filterResultsChange.emit and part of the filterResults array
    //this.cropYear.emit(this.selectedCropYear[0]);

    this.filterResults.selectedShipmentStatuses = this.selectedShipmentStatuses;
    this.filterResults.selectedCropYears = this.selectedCropYear;
    this.filterResults.selectedWarehouses = this.selectedWarehouses;

    this.filterResults.selectedLotNumbers = this.selectedLotNumber;
    this.filterResults.selectedInventoryItems = this.selectedInventoryItem;
    this.filterResults.selectedLocations = this.selectedLocation;

    this.filterResults.selectedRooms = this.selectedRooms;
    this.filterResults.selectedOrganConventional = this.selectedOrganicConventional;
    this.filterResults.selectedBlocks = this.selectedBlocks;

    this.filterResults.selectedFarms = this.selectedFarms;
    this.filterResults.selectedVarieties = this.selectedVarieties;
    this.filterResults.selectedFieldEventType = this.selectedFieldEventType;
    this.filterResults.selectedFieldmen = this.selectedFieldmen;

    this.filterResults.selectedUserNames = this.selectedUserName;
    this.filterResults.selectedBreweries = this.selectedBrewery;

    this.filterResults.selectedQcAreas = this.selectedQcAreas;
    this.filterResults.selectedEstimateTypes = this.selectedEstimateTypes;
    this.filterResults.selectedRooms = this.selectedRooms;
    this.filterResults.selectedCarriers = this.selectedCarrier;
    this.filterResults.selectedTruckTypes = this.selectedTruckType;
    this.filterResults.selectedDateRange = this.selectedDates;
    this.filterResults.selectedGrouping = this.selectedGrouping;

    // this.filterResultsChange.emit(this.filterResults); 
    this.hasLoaded = true;
    this._itemChangedSource.next(null);
    this.isLoading = false;

  }

  public filter(event: any = null) {
    this.filterBlocks();
    this.filterRooms();
    this.filterRoomLocations();

    this.filterFieldman();
    this.filterEstimateTypes();
    this.change();

  }

  selectDates() {
    this.filterResults.selectedDateRange = this.selectedDates;
    this.filter();

  }

  change() {
    this.filterResults.selectedBlocks = this.selectedBlocks;

    this.filterResults.selectedWorkOrderTypes = this.selectedWorkOrderTypes;

    this.filterResults.selectedStorageTypes = this.selectedStorageTypes;

    this.filterResults.selectedPools = this.selectedPoolTypes;

    this.filterResults.selectedGrouping = this.selectedGrouping;

    this.filterResults.selectedBlockCustomFieldValue = this.selectedBlockCustomFieldValue;

    this.filterResults.selectedFeatureEventTypes = this.allFeatureEventTypes.filter(x => this.selectedFeatureEventType.map(y => y.id).includes(x.id));
    this.filterResults.selectedFeatureTypes = this.allFeatureTypes.filter(x => this.selectedFeatureTypes.map(y => y.id).includes(x.farmFeatureTypeId));
    this.filterResults.selectedFeatures = this.allFeatures.filter(x => this.selectedFeatures.map(y => y.id).includes(x.farmFeatureId));

    this.filterResults.selectedCropYears = this.selectedCropYear.filter(x => x.id != 0);
    this.filterResults.selectedFarms = this.selectedFarms.filter(x => x.id != 0);
    this.filterResults.selectedFieldmen = this.selectedFieldmen;

    this.filterResults.selectedUserNames = this.selectedUserName;
    this.filterResults.selectedBreweries = this.selectedBrewery;

    this.filterResults.selectedCarriers = this.selectedCarrier;
    this.filterResults.selectedTruckTypes = this.selectedTruckType;
    this.filterResults.selectedLoad = this.selectedLoad;

    this.filterResults.selectedVarieties = this.selectedVarieties;
    this.filterResults.selectedFieldEventType = this.selectedFieldEventType;
    this.filterResults.selectedOrganConventional = this.selectedOrganicConventional;
    this.filterResults.selectedQcAreas = this.selectedQcAreas;
    this.filterResults.selectedEstimateTypes = this.allEstimateTypes.filter(x => this.selectedEstimateTypes.map(y => y.id).includes(x.id));
    this.filterResults.selectedWarehouses = this.allWarehouses.filter(x => this.selectedWarehouses.map(y => y.id).includes(x.id));

    this.filterResults.selectedInventoryItems = this.allInventoryItems.filter(x => this.selectedInventoryItem.map(y => y.id).includes(x.id));
    this.filterResults.selectedLotNumbers = this.allLots.filter(x => this.selectedLotNumber.map(y => y.id).includes(x.id));
    this.filterResults.selectedLocations = this.allLocations.filter(x => this.selectedLocation.map(y => y.id).includes(x.id));


    this.filterResults.selectedRooms = this.allRooms.filter(x => this.selectedRooms.map(y => y.id).includes(x.id));
    this.filterResults.selectedDateRange = [...this.selectedDates];
    this.filterResults.selectedShipmentStatuses = this.selectedShipmentStatuses;

    // Ensure customer and barcode are always up to date in filterResults
    this.filterResults.selectedCustomers = this.selectedCustomer;
    this.filterResults.barcode = this.barcode;

    if (this.filterResults.selectedEstimateTypes.length == 0)
      this.filterResults.selectedEstimateTypes = [{ id: 0, name: 'Final' }]

    if (this.selectedCompleted.length > 0)
    {
      this.filterResults.includeCompleted = this.selectedCompleted[0].id;
      if(this.selectedCompleted[0].id == 'isLast'){
        this.filterResults.selectedCropYears = this.listOfYears.filter(f => f.itemName == new Date().getFullYear().toString()) 
      }
    }

    this._itemChangedSource.next(null);

    // Save filters to localStorage when retain filters is checked
    this.saveFiltersToStorage();
  }

  filterEstimateTypes() {
    // estimate types
    this.listOfEstimateTypes = _.sortBy(this.allEstimateTypes.filter(x => x.cropId == this.selectedCrop[0].id), x => x.order).map(x => <FilterItem>{
      id: x.id,
      itemName: x.name
    });
    this.listOfEstimateTypes = [...this.listOfEstimateTypes, { id: 0, itemName: 'Final' }];
  }


  filterBlocks() {
    let customFieldValues = [];
    this.selectedBlockCustomFieldValue.forEach(x => x.data.forEach(d => customFieldValues.push(d)));

    let blockMap = [...this.allBlocks]
      .filter(x =>
      (
        (this.selectedFarms.map(y => y.id).includes(x.farmId) || this.selectedFarms.length == 0) &&
        (this.selectedVarieties.map(y => y.id).includes(x.varietyId) || this.selectedVarieties.length == 0) &&
        (this.selectedFieldmen.map(y => y.id).includes(x.fieldman || '') || this.selectedFieldmen.length == 0) &&
        //(this.selectedBlocks.map(y => y.id).includes(x.id) || this.selectedBlocks.length == 0) &&
        (this.selectedCrop.map(y => y.id).includes(x.cropId) || this.selectedCrop.length == 0) //&&

        //(customFieldValues.map(y => y.itemName).includes(x.customFields.map(z => z.value)) || customFieldValues.length == 0)
      )
      )
      .map(x => {
        return {
          id: x.id,
          itemName: x.name + " (" + x.varietyName + ")",
          group: x.farmName
        };
      });

    let uniqBlocks = _.uniq(blockMap, x => x.id);
    this.listOfBlocks = _.chain(uniqBlocks).sortBy(x => x.itemName).sortBy(x => x.group).value();
  }

  filterRooms() {
    let roomMap = [...this.allRooms]
      .filter(x =>
        (this.selectedWarehouses.map(y => y.id).includes(x.warehouse && x.warehouse.id) || this.selectedWarehouses.length == 0)
      )
      .map(x => {
        return {
          id: x.id,
          itemName: x.name,
          group: x.warehouse && x.warehouse.name
        };
      });

    let uniqBlocks = _.uniq(roomMap, x => x.id);
    this.listOfRooms = _.chain(uniqBlocks).sortBy(x => x.itemName).sortBy(x => x.group).value();
  }

  filterRoomLocations() {

    let locationMap = [];
    if (this.selectedRooms.length == 0)
      locationMap = [...this.allLocations]
        .filter(x =>
          (this.listOfRooms.map(y => y.id).includes(x.roomId))
        )
        .map(x => {
          return {
            id: x.id,
            itemName: x.name,
            group: x.room.name
          };
        });

    else
      locationMap = [...this.allLocations]
        .filter(x =>
          (this.selectedRooms.map(y => y.id).includes(x.roomId) || this.selectedRooms.length == 0)
        )
        .map(x => {
          return {
            id: x.id,
            itemName: x.name,
            group: x.room.name
          };
        });

    let uniqBlocks = _.uniq(locationMap, x => x.id);
    this.listOfLocations = _.chain(uniqBlocks).sortBy(x => x.itemName).sortBy(x => x.group).value();
  }

  filterFieldman() {
    let fieldmanMap = [...this.allBlocks]
      .filter(x =>
      (
        (this.selectedFarms.map(y => y.id).includes(x.farmId) || this.selectedFarms.length == 0) &&
        (this.selectedVarieties.map(y => y.id).includes(x.varietyId) || this.selectedVarieties.length == 0) &&
        (this.selectedFieldmen.map(y => y.id).includes(x.fieldman || '') || this.selectedFieldmen.length == 0) &&
        (this.selectedBlocks.map(y => y.id).includes(x.id) || this.selectedBlocks.length == 0))
      )
      .map(x => {
        return {
          id: x.fieldman,
          itemName: x.fieldman || 'None',
        };
      });

    let uniqFieldmans = _.uniq(fieldmanMap, x => x.id);
    this.listOfFieldmen = _.chain(uniqFieldmans).sortBy(x => x.itemName).sortBy(x => x.itemName).value();

  }

  filterCropYearByCropID() {

    this.listOfYears = [...this.allYears.filter(x => x.cropId === this.selectedCrop[0].id).map(y => {
      return {
        id: y.id,
        itemName: y.year.toString(),
        isActive: y.isCurrentYear,
      }
    })]
    this.listOfYears = _.sortBy(this.listOfYears, 'itemName').reverse();

    // Guard empty selection/list so we never end up with [undefined] (which sends an undefined cropYearId)
    const selectedYearName = this.selectedCropYear.length > 0 && this.selectedCropYear[0] ? this.selectedCropYear[0].itemName : null;
    if (this.listOfYears.filter(x => x.itemName === selectedYearName).length > 0) {
      this.selectedCropYear = this.listOfYears.filter(x => x.itemName === selectedYearName)
    } else {
      this.selectedCropYear = this.listOfYears.length > 0 ? [this.listOfYears[0]] : [];
    }

    // Filter varieties by selected crop (only if varieties have been loaded)
    if (this.allVarieties && this.allVarieties.length > 0) {
      if (this.cropFilterVisible && this.selectedCrop && this.selectedCrop.length > 0 && this.selectedCrop[0].id !== 0) {
        const filteredVarieties = this.allVarieties.filter(x => x.cropId === this.selectedCrop[0].id);
        const varietyMap = filteredVarieties.map(x => ({
          id: x.id,
          itemName: this.useVarietyShortName ? x.varietyShortName : x.name
        }));
        const uniqVarieties = _.uniq(varietyMap, x => x.id);
        this.listOfVarieties = _.sortBy(uniqVarieties, x => x.itemName);

        // Clear selected varieties that are not in the filtered list
        const varietyIds = this.listOfVarieties.map(v => v.id);
        this.selectedVarieties = this.selectedVarieties.filter(v => varietyIds.includes(v.id));
      } else {
        // Reset to all varieties if no crop selected
        const varietyMap = this.allVarieties.map(x => ({
          id: x.id,
          itemName: this.useVarietyShortName ? x.varietyShortName : x.name
        }));
        const uniqVarieties = _.uniq(varietyMap, x => x.id);
        this.listOfVarieties = _.sortBy(uniqVarieties, x => x.itemName);
      }
    }

    this.filter();
  }


  /// Properties
  public selectedDates: Date[] = [null, null];
  public isLoading: boolean = true;
  public hasLoaded: boolean = false;
  private isLoadingData: boolean = false;
  public filterRetainerRoutes: FilterRetainerRoute[] = [
    {
      sourceUrl: '/harvest-engine/estimates/estimates-list',
      destinationUrl: '/harvest-engine/pick-plan/pick-plan-edit',
      bothDirections: true
    },
    {
      sourceUrl: '/harvest-engine/inventory/inventory-summary',
      destinationUrl: '/harvest-engine/inventory/inventory-summary-popup',
      bothDirections: true
    }
  ]

  public isHops: boolean = false;
  public bypassFilterReset: boolean = true;
  public initialLoad = true;



  public loadSearchValue: string;
  private defaultSelectedList = [{ itemName: 'Select', id: 0 }];

  private _itemChangedSource = new BehaviorSubject(null);
  public itemChanged = this._itemChangedSource.asObservable();


  public filterResults: FilterResults = {
    selectedBlocks: [],
    selectedBlockCustomFieldValue: [],
    selectedCropYears: [],
    selectedFarms: [],
    selectedFieldmen: [],
    selectedVarieties: [],
    selectedOrganConventional: [],
    selectedDateRange: [null,null],
    selectedQcAreas: [],
    includeCompleted: false,
    selectedCarriers: [],
    selectedTruckTypes: [],
    selectedLoad: "",
    selectedInventoryItems: [],
    selectedLocations: [],
    selectedLotNumbers: [],
    selectedBreweries: [],
    selectedUserNames: [],
    selectedFeatureEventTypes: [],
    selectedFeatureTypes: [],
    selectedFeatures: [],
    selectedGrouping: [],
    selectedStorageTypes: [],
    selectedWorkOrderTypes: [],
    selectedCustomers: [],
    barcode: ""
  };

  public allWorkOrderTypes: WorkOrderType[] = [];
  public selectedFarms: FilterItem[] = [];
  public listOfFarms: FilterItem[] = [];
  public listOfWorkOrderTypes: FilterItem[] = [];
  public selectedWorkOrderTypes: FilterItem[] = [];
  public farmDropdownSettings = {
    singleSelection: false,
    text: this.pipe.transform("Farm") + "s",
    selectAllText: 'Select All',
    unSelectAllText: 'Clear All',
    enableSearchFilter: true,
    classes: "",
    noDataLabel: "No data found",
    badgeShowLimit: 1,
  };


  public selectedBlocks: FilterItem[] = [];
  public listOfBlocks: FilterItem[] = [];
  public allBlocks: BlockDTO[] = [];

  public selectedBlockCustomFieldValue: BlockCustomFieldDropdownValue[] = [];
  public listOfBlockCustomFieldValues: FilterItem[] = [];
  private allBlockCustomFieldValues: BlockCustomFieldValueDTO[] = [];
  public customBlockFields: BlockCustomFieldDropdownValue[] = [];
  public allCustomBlockFieldValues: BlockCustomFieldDropdownValue[] = [];

  public selectedRooms: FilterItem[] = [];
  public listOfRooms: FilterItem[] = [];
  public allRooms: Room[] = [];


  public selectedWarehouses: FilterItem[] = [];
  public listOfWarehouses: FilterItem[] = [];
  public allWarehouses: Warehouse[] = [];

  public selectedInventoryItem: FilterItem[] = [];
  public listOfInventoryItems: FilterItem[] = [];
  public allInventoryItems: InventoryItemDTO[] = [];

  public selectedLocation: FilterItem[] = [];
  public listOfLocations: FilterItem[] = [];
  public allLocations: RoomLocation[] = [];

  public selectedLotNumber: FilterItem[] = [];
  public listOfLotNumbers: FilterItem[] = [];
  public allLots: InventoryLotDTO[] = [];

  public selectedEstimateTypes: FilterItem[] = [{ itemName: 'Final', id: 0 }];
  public listOfEstimateTypes: FilterItem[] = [];
  public allEstimateTypes: estimateTypeDTO[] = [];

  public selectedPoolTypes: FilterItem[] = [];
  public listOfPoolTypes: FilterItem[] = [];
  public allPoolTypes: PoolTypeDTO[] = [];

  public allVarieties: CropVariety[] = [];
  public allFieldEventTypes: FieldEventType[] = [];

  public selectedVarieties: FilterItem[] = [];
  public selectedFieldEventType: FilterItem[] = [];
  public listOfVarieties: FilterItem[] = [];
  public listOfFieldEventTypes: FilterItem[] = [];


  public allFeatures: FarmFeature[] = [];
  public selectedFeatures: FilterItem[] = [];
  public listOfFeatures: FilterItem[] = [];

  public allFeatureTypes: FarmFeatureType[] = [];
  public selectedFeatureTypes: FilterItem[] = [];
  public listOfFeatureTypes: FilterItem[] = [];

  public selectedFieldmen: FilterItem[] = [];
  public listOfFieldmen: FilterItem[] = [];

  public selectedUserName: FilterItem[] = [];
  public listOfUserNames: FilterItem[] = [];
  public allUserNames: Cart[] = [];

  public selectedBrewery: FilterItem[] = [];
  public listOfBreweries: FilterItem[] = [];
  public allBreweries: Brewery[] = [];

  public allFeatureEventTypes: FarmFeatureEventType[] = [];
  public selectedFeatureEventType: FilterItem[] = [];

  public selectedCarrier: FilterItem[] = [];
  public listOfCarriers: FilterItem[] = [];
  private allCarriers: Carrier[] = [];

  public selectedTruckType: FilterItem[] = [];
  public listOfTruckTypes: FilterItem[] = [];
  private allTruckTypes: TruckType[] = [];

  public selectedLoad: string = "";

  public allGroupings: FilterItem[] = [];

  public selectedGrouping: FilterItem[] = [];

  // public selectedCrop: FilterItem[] = [];
  public selectedCrop: FilterItem[] = [{ itemName: 'Select', id: 0 }];
  public listOfCrops: FilterItem[] = [];
  public allCrops: Crop[] = [];

  public selectedCustomer: FilterItem[] = [];
  public listOfCustomers: FilterItem[] = [];
  public allCustomers: Customer[] = [];

  public barcode: string = "";

  // STORAGE TYPES //
  public selectedStorageTypes: FilterItem[] = [];
  public listOfStorageTypes: FilterItem[] = [];
  private allStorageTypes: StorageTypeDTO[] = [];

  public selectedTypes: FilterItem[] = [];
  public listOfTypes: FilterItem[] = [];

  defaultShipmentStatuses: FilterItem[] = [{
    id: 1,
    itemName: 'Shipment Scheduled'
  },
  {
    id: 2,
    itemName: 'Accepted'
  },
  {
    id: 3,
    itemName: 'Expired'
  },
  {
    id: 4,
    itemName: 'Picked Up'
  },
  {
    id: 6,
    itemName: 'Rescheduled'
  },
  {
    id: 7,
    itemName: 'Updated'
  },
  ];

  public selectedShipmentStatuses: FilterItem[] = this.defaultShipmentStatuses;

  public listOfShipmentStatuses: FilterItem[] = [
    {
      id: 1,
      itemName: 'Shipment Scheduled'
    },
    {
      id: 2,
      itemName: 'Accepted'
    },
    {
      id: 3,
      itemName: 'Expired'
    },
    {
      id: 4,
      itemName: 'Picked Up'
    },
    {
      id: 5,
      itemName: 'Received'
    },
    {
      id: 6,
      itemName: 'Rescheduled'
    },
    {
      id: 7,
      itemName: 'Updated'
    },
  ];

  public listOfYears: FilterItem[] = [];
  public allYears: CropYear[] = [];

  defaultCropYear: number;
  public selectedCropYear: FilterItem[] = [];

  
  public listOfOrganic: FilterItem[] = [
    { itemName: 'Organic', id: true },
    { itemName: 'Conventional', id: false }
  ];
    
  public selectedOrganicConventional: FilterItem[] = [];

  public selectedCompleted: FilterItem[] = [];
  public listOfCompleted: FilterItem[] = [
    { itemName: 'Yes', id: true },
    { itemName: 'No', id: false },
    { itemName: 'Last 7 days', id: 'isLast'}
  ];




  public selectedQcAreas: FilterItem[] = [];
  public listOfQcAreas: FilterItem[] = [];
  public allQcAreas: QcArea[] = [];
  public listOfFeatureEventTypes: FilterItem[] = [];
}
